import { useEffect, useRef } from "react";

/**
 * Touch/drag controllable infinite marquee using GPU-friendly transforms.
 * Uses translate3d on inner track (no native scroll) so it never fights with
 * mobile browser momentum scrolling. Vertical page scroll stays smooth thanks
 * to touch-action: pan-y.
 */
export const Marquee = ({
  items,
  renderItem,
  speed = 0.6,
  direction = "left",
  className = "",
  innerClassName = "",
  testId,
  ariaLabel,
}) => {
  const containerRef = useRef(null);
  const trackRef = useRef(null);
  const posRef = useRef(0);
  const halfRef = useRef(0);
  const dirRef = useRef(direction === "left" ? 1 : -1);
  const pausedRef = useRef(false);
  const rafRef = useRef(0);
  const dragRef = useRef(null);
  const resumeTimerRef = useRef(null);
  const activePointerRef = useRef(null);
  const wasDraggingRef = useRef(false);

  const applyTransform = () => {
    if (trackRef.current) {
      trackRef.current.style.transform = `translate3d(${-posRef.current}px, 0, 0)`;
    }
  };

  useEffect(() => {
    const track = trackRef.current;
    if (!track) return;

    const measure = () => {
      halfRef.current = track.scrollWidth / 2;
    };
    measure();

    // Re-measure when images finish loading
    const imgs = Array.from(track.querySelectorAll("img"));
    const onImgLoad = () => measure();
    imgs.forEach((img) => {
      if (!img.complete) img.addEventListener("load", onImgLoad);
    });

    const ro = new ResizeObserver(measure);
    ro.observe(track);

    const step = () => {
      if (!pausedRef.current && halfRef.current > 0) {
        posRef.current += speed * dirRef.current;
        if (posRef.current >= halfRef.current) posRef.current -= halfRef.current;
        if (posRef.current < 0) posRef.current += halfRef.current;
        applyTransform();
      }
      rafRef.current = requestAnimationFrame(step);
    };
    rafRef.current = requestAnimationFrame(step);

    return () => {
      cancelAnimationFrame(rafRef.current);
      ro.disconnect();
      imgs.forEach((img) => img.removeEventListener("load", onImgLoad));
      if (resumeTimerRef.current) clearTimeout(resumeTimerRef.current);
    };
  }, [speed]);

  const onPointerDown = (e) => {
    // Only respond to primary pointer
    if (activePointerRef.current !== null) return;
    activePointerRef.current = e.pointerId;
    pausedRef.current = true;
    dragRef.current = {
      x: e.clientX,
      y: e.clientY,
      startPos: posRef.current,
      lastDx: 0,
      decided: false,
      isHorizontal: false,
    };
    if (resumeTimerRef.current) {
      clearTimeout(resumeTimerRef.current);
      resumeTimerRef.current = null;
    }
    try { e.currentTarget.setPointerCapture?.(e.pointerId); } catch (_err) { /* noop */ }
  };

  const onPointerMove = (e) => {
    const drag = dragRef.current;
    const track = trackRef.current;
    if (!drag || !track) return;
    if (e.pointerId !== activePointerRef.current) return;

    const dx = e.clientX - drag.x;
    const dy = e.clientY - drag.y;

    // On touch, decide axis after small movement so vertical swipes
    // remain page scrolls and only horizontal swipes drive the marquee.
    if (!drag.decided && e.pointerType !== "mouse") {
      if (Math.abs(dx) < 6 && Math.abs(dy) < 6) return;
      drag.decided = true;
      drag.isHorizontal = Math.abs(dx) > Math.abs(dy);
      if (!drag.isHorizontal) {
        // Vertical swipe — release control so page scrolls naturally
        try { e.currentTarget.releasePointerCapture?.(e.pointerId); } catch (_err) { /* noop */ }
        activePointerRef.current = null;
        dragRef.current = null;
        if (resumeTimerRef.current) clearTimeout(resumeTimerRef.current);
        resumeTimerRef.current = setTimeout(() => { pausedRef.current = false; }, 200);
        return;
      }
    }

    if (e.pointerType !== "mouse" && drag.decided && !drag.isHorizontal) return;

    drag.lastDx = dx;
    let next = drag.startPos - dx;
    const half = halfRef.current;
    if (half > 0) {
      while (next >= half) next -= half;
      while (next < 0) next += half;
    }
    posRef.current = next;
    applyTransform();

    // Prevent the page from scrolling horizontally / interpreting as swipe gesture
    if (e.cancelable && (drag.isHorizontal || e.pointerType === "mouse")) {
      e.preventDefault();
    }
  };

  const finishDrag = (e) => {
    const drag = dragRef.current;
    if (drag) {
      if (Math.abs(drag.lastDx) > 4) {
        wasDraggingRef.current = true;
        setTimeout(() => { wasDraggingRef.current = false; }, 80);
      }
      if (drag.lastDx > 6) dirRef.current = -1;
      else if (drag.lastDx < -6) dirRef.current = 1;
      try { e?.currentTarget?.releasePointerCapture?.(e.pointerId); } catch (_err) { /* noop */ }
    }
    activePointerRef.current = null;
    dragRef.current = null;
    if (resumeTimerRef.current) clearTimeout(resumeTimerRef.current);
    resumeTimerRef.current = setTimeout(() => {
      pausedRef.current = false;
    }, 350);
  };

  const onClickCapture = (e) => {
    if (wasDraggingRef.current) {
      e.preventDefault();
      e.stopPropagation();
    }
  };

  return (
    <div
      ref={containerRef}
      data-testid={testId}
      role="region"
      aria-label={ariaLabel}
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={finishDrag}
      onPointerCancel={finishDrag}
      onClickCapture={onClickCapture}
      className={`overflow-hidden cursor-grab active:cursor-grabbing select-none ${className}`}
      style={{
        touchAction: "pan-y",
        WebkitUserSelect: "none",
        userSelect: "none",
        WebkitTapHighlightColor: "transparent",
      }}
    >
      <div
        ref={trackRef}
        className={`flex w-max will-change-transform ${innerClassName}`}
        style={{ transform: "translate3d(0,0,0)" }}
      >
        {[...items, ...items].map((it, i) => (
          <div
            key={i}
            className="shrink-0"
            data-marquee-index={i}
            style={{ WebkitUserDrag: "none" }}
          >
            {renderItem(it, i % items.length)}
          </div>
        ))}
      </div>
    </div>
  );
};
